import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { LogOut, User, Users, LayoutDashboard, AlertTriangle, Settings, FileText, UtensilsCrossed, Image, Inbox, CalendarDays, BookOpen, Armchair, SlidersHorizontal } from "lucide-react";
import Brand from "@/components/Brand";
import { useAuth } from "@/contexts/AuthContext";
import { useMaintenance } from "@/contexts/MaintenanceContext";
import { Button } from "@/components/ui/button";

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isSuper = user?.role === "superadmin";
  const { enabled: maintenanceOn } = useMaintenance();

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  const navItemCls = ({ isActive }) =>
    `flex items-center gap-2 px-3 py-2 rounded-md text-sm transition ${
      isActive
        ? "bg-primary/10 text-primary border border-primary/30"
        : "text-muted-foreground hover:text-foreground hover:bg-secondary border border-transparent"
    }`;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="border-b border-border/60 bg-background/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/admin" className="flex items-center" data-testid="admin-home-link">
            <Brand size="sm" subtitle={false} />
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground hidden sm:inline" data-testid="header-user-email">
              {user?.email}
            </span>
            <span
              className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border border-primary/40 text-primary"
              data-testid="header-user-role"
            >
              {user?.role}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              data-testid="logout-button"
              className="border-border hover:border-primary/40"
            >
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      {maintenanceOn && (
        <div
          data-testid="maintenance-banner"
          role="status"
          aria-live="polite"
          className="bg-destructive/20 border-b border-destructive/40 text-destructive-foreground"
        >
          <div className="max-w-6xl mx-auto px-5 sm:px-8 py-3 flex items-center gap-3 text-sm">
            <AlertTriangle className="w-4 h-4 shrink-0 text-destructive" />
            <span className="flex-1">
              <span className="font-semibold text-destructive">Site is in maintenance mode</span>{" "}
              — public visitors see the maintenance screen.{" "}
              {isSuper && (
                <Link
                  to="/admin"
                  className="underline text-destructive hover:text-destructive/80"
                  data-testid="maintenance-banner-disable"
                >
                  Click to disable
                </Link>
              )}
            </span>
          </div>
        </div>
      )}

      {user?.force_2fa_reenroll && (
        <div
          data-testid="reenroll-banner"
          className="bg-amber-500/10 border-b border-amber-500/30 text-amber-200"
        >
          <div className="max-w-6xl mx-auto px-5 sm:px-8 py-3 flex items-center gap-3 text-sm">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>
              2FA was disabled via recovery. Please{" "}
              <Link to="/admin/profile" className="underline text-amber-100">
                re-enable 2FA from your profile
              </Link>{" "}
              now.
            </span>
          </div>
        </div>
      )}

      <div className="flex-1 max-w-6xl w-full mx-auto px-5 sm:px-8 py-10 grid grid-cols-1 md:grid-cols-[220px_1fr] gap-8">
        <aside>
          <nav className="flex md:flex-col gap-1" data-testid="admin-nav">
            <NavLink to="/admin" end className={navItemCls} data-testid="nav-dashboard">
              <LayoutDashboard className="w-4 h-4" /> Dashboard
            </NavLink>
            <NavLink to="/admin/profile" className={navItemCls} data-testid="nav-profile">
              <User className="w-4 h-4" /> Profile
            </NavLink>
            {isSuper && (
              <NavLink to="/admin/users" className={navItemCls} data-testid="nav-users">
                <Users className="w-4 h-4" /> Users
              </NavLink>
            )}
            <div className="mt-3 pt-3 border-t border-border/40 flex md:flex-col gap-1">
              <NavLink to="/admin/settings" className={navItemCls} data-testid="nav-settings">
                <Settings className="w-4 h-4" /> Site settings
              </NavLink>
              <NavLink to="/admin/pages" className={navItemCls} data-testid="nav-pages">
                <FileText className="w-4 h-4" /> Pages
              </NavLink>
              <NavLink to="/admin/menu" className={navItemCls} data-testid="nav-menu">
                <UtensilsCrossed className="w-4 h-4" /> Menu
              </NavLink>
              <NavLink to="/admin/media" className={navItemCls} data-testid="nav-media">
                <Image className="w-4 h-4" /> Media
              </NavLink>
              <NavLink to="/admin/contact" className={navItemCls} data-testid="nav-contact">
                <Inbox className="w-4 h-4" /> Contact inbox
              </NavLink>
            </div>
            <div className="mt-3 pt-3 border-t border-border/40 flex md:flex-col gap-1">
              <NavLink to="/admin/events" className={navItemCls} data-testid="nav-events">
                <CalendarDays className="w-4 h-4" /> Events
              </NavLink>
              <NavLink to="/admin/bookings" className={navItemCls} data-testid="nav-bookings">
                <BookOpen className="w-4 h-4" /> Bookings
              </NavLink>
              <NavLink to="/admin/tables" className={navItemCls} data-testid="nav-tables">
                <Armchair className="w-4 h-4" /> Tables
              </NavLink>
              <NavLink to="/admin/booking-settings" className={navItemCls} data-testid="nav-booking-settings">
                <SlidersHorizontal className="w-4 h-4" /> Booking settings
              </NavLink>
            </div>
          </nav>
        </aside>
        <main className="min-w-0">{children}</main>
      </div>

      <footer className="border-t border-border/60 py-6">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 text-xs text-muted-foreground flex justify-between">
          <span>© {new Date().getFullYear()} Caffe Bar Kings</span>
          <span className="hidden sm:inline">Ulica Josipa Šajnovića 2/A, 40315 Mursko Središće</span>
        </div>
      </footer>
    </div>
  );
}
